/**
 * 화면 4 · 착석 완료 (SeatedScreen) — 디자인 원본 Pantalla 4.
 *
 * "Ver el menú" 가 매장 메뉴로 이어져 줄서기 → 주문 전환을 만든다 (설계 §6.1).
 *
 * 테이블(좌석) 대기 전용 화면이다 — 픽업 큐의 `seated` 는 FilaVirtualPage 가
 * {@link WaitlistDeliveredScreen} 으로 분기하므로 이 화면에는 도달하지 않는다
 * (#794 리뷰 — 과거 존재하던 pk 분기는 도달 불가 dead code 라 제거했다).
 *
 * @see #781 / admin#2111
 */
import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { WaitlistPartyData, WaitlistStatusData } from '@/lib/api/waitlist';
import { WaitlistSegments } from './WaitlistParts';
import { WaitlistMenuButton } from './WaitlistMenuButton';
import type { WaitlistStrings } from './strings';

interface WaitlistSeatedScreenProps {
  t: WaitlistStrings;
  party: WaitlistPartyData;
  onViewMenu: () => void;
  onReset: () => void;
  /** 있으면 "메뉴 보기" 버튼(외부 링크)을 추가로 노출한다(#794 통합). */
  storeMeta?: WaitlistStatusData['store_meta'];
}

export function WaitlistSeatedScreen({
  t,
  party,
  onViewMenu,
  onReset,
  storeMeta,
}: WaitlistSeatedScreenProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-7 py-10 text-center">
      <div className="mb-[22px] grid size-[84px] place-items-center rounded-full bg-primary text-primary-foreground">
        <Check className="size-[42px]" />
      </div>
      <h1 className="m-0 mb-2 text-[26px] font-extrabold leading-tight">{t.bienvenido}</h1>
      {
        // table_label 은 파트너가 착석 시 입력하는 선택 값이라 없을 수 있다 —
        // 그 경우 테이블 문장을 통째로 생략하고 환영 + 상태 배지만 보인다.
        party.table_label && (
          <p className="m-0 mb-1.5 text-[15px] leading-relaxed text-muted-foreground">
            <WaitlistSegments segments={t.teAsignamos(party.table_label, party.party_size)} />
          </p>
        )
      }
      <Badge variant="secondary" className="mb-8">
        {t.atendido(party.ticket_no)}
      </Badge>
      <div className="flex w-full max-w-[300px] flex-col gap-2.5">
        <Button size="lg" className="w-full" onClick={onViewMenu}>
          {t.verMenu}
        </Button>
        {storeMeta && (
          <WaitlistMenuButton
            t={t}
            menuViewUrl={storeMeta.menu_view_url}
            menuViewKind={storeMeta.menu_view_kind}
            menuUrl={storeMeta.menu_urls?.digital}
            menuPdfUrl={storeMeta.menu_urls?.pdf}
          />
        )}
        <Button variant="ghost" className="w-full text-muted-foreground" onClick={onReset}>
          {t.volverInicio}
        </Button>
      </div>
    </div>
  );
}
